import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Automation } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { Badge, Button, Card, EmptyState, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label } from '../lib/format.js';

const TEMPLATES: { name: string; trigger: Automation['trigger']; actions: Automation['actions'] }[] = [
  { name: 'Ask for a review when a job is completed', trigger: 'job_completed', actions: [{ type: 'request_review', params: { reviewUrl: 'https://g.page/r/your-review-link' } }] },
  { name: 'Text a reminder on overdue invoices', trigger: 'invoice_overdue', actions: [{ type: 'send_sms', params: { body: 'Hi {{customer}}, a reminder that your invoice is past due.' } }] },
  { name: 'Create a follow-up when an estimate is sent', trigger: 'estimate_sent', actions: [{ type: 'create_follow_up', params: { note: 'Follow up on the estimate for {{customer}}.' } }] },
];

export function Automations() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['automations'], queryFn: () => api.get<{ items: Automation[] }>('/automations') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['automations'] });

  const toggle = async (a: Automation) => {
    try { await api.patch(`/automations/${a.id}`, { enabled: !a.enabled }); refresh(); }
    catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Failed', 'error'); }
  };
  const addTemplate = async (t: typeof TEMPLATES[number]) => {
    try { await api.post('/automations', { name: t.name, trigger: t.trigger, actions: t.actions }); refresh(); toast.push('Automation added', 'success'); }
    catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Failed', 'error'); }
  };

  const existingTriggers = new Set((data?.items ?? []).map((a) => a.name));

  return (
    <>
      <div className="page-head"><div><div className="page-title">Automations</div><div className="page-sub">The system follows up so your office doesn't have to</div></div></div>

      <Card style={{ marginBottom: 'var(--sp-5)' }}>
        <div className="card-head"><span className="card-title">Active automations</span></div>
        <div style={{ padding: '0 var(--sp-5)' }}>
          {isLoading ? <div className="card-pad"><Skeleton h={60} /></div>
            : (data?.items.length ?? 0) === 0 ? <EmptyState icon="⚡" title="No automations yet">Add one from a template below.</EmptyState>
            : data!.items.map((a) => (
              <div key={a.id} className="queue-item">
                <div className="stack">
                  <div className="row gap-2"><strong style={{ fontSize: 'var(--text-sm)' }}>{a.name}</strong>{a.conditions.length > 0 && <Badge tone="neutral">{a.conditions.length} condition{a.conditions.length > 1 ? 's' : ''}</Badge>}</div>
                  <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>When <b>{label(a.trigger)}</b> → {a.actions.map((ac) => label(ac.type)).join(', ')}</span>
                </div>
                <button className={`btn ${a.enabled ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => toggle(a)}>{a.enabled ? 'On' : 'Off'}</button>
              </div>
            ))}
        </div>
      </Card>

      <Card>
        <div className="card-head"><span className="card-title">Add from a template</span></div>
        <div style={{ padding: '0 var(--sp-5)' }}>
          {TEMPLATES.map((t) => (
            <div key={t.name} className="queue-item">
              <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{t.name}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>When {label(t.trigger)} → {t.actions.map((ac) => label(ac.type)).join(', ')}</span></div>
              <Button size="sm" variant="secondary" disabled={existingTriggers.has(t.name)} onClick={() => addTemplate(t)}>{existingTriggers.has(t.name) ? 'Added' : 'Add'}</Button>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
